import React, { useState, useEffect } from "react";
import { Chart } from "primereact/chart";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { ProgressBar } from "primereact/progressbar";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import PageHeader from "../components/common/PageHeader";
import LoadingSpinner from "../components/LoadingSpinner";
import {
  getMessagesTimeline,
  getUsageStats,
  getMessageStatusBreakdown,
} from "../services/analyticsService";
import {
  TrendingUp,
  Users,
  MessageSquare,
  FolderOpen,
  Calendar,
} from "lucide-react";

const Analytics = () => {
  const [timelineData, setTimelineData] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("daily");
  const [range, setRange] = useState(30);

  const periodOptions = [
    { label: "Daily", value: "daily" },
    { label: "Monthly", value: "monthly" },
  ];

  const rangeOptions = [
    { label: "Last 7 days", value: 7 },
    { label: "Last 30 days", value: 30 },
    { label: "Last 90 days", value: 90 },
    { label: "Last 12 months", value: 12 },
  ];

  useEffect(() => {
    fetchAnalyticsData();
  }, [period, range]); // eslint-disable-line

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const [timeline, usage, status] = await Promise.all([
        getMessagesTimeline(period, range),
        getUsageStats(),
        getMessageStatusBreakdown(),
      ]);

      setTimelineData(timeline.data);
      setUsageData(usage.data);
      setStatusData(status.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  // Chart 1: Messages Timeline (Line Chart)
  const timelineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: false,
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  // Chart 3: Status Breakdown (Doughnut Chart)
  const statusChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  if (loading) {
    return (
      <LoadingSpinner fullScreen={true} size="lg" text="Loading analytics..." />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <PageHeader
          showBackButton={true}
          title="Analytics & Insights"
          subtitle="Track your messaging performance and subscription usage"
        />

        {/* Summary Cards */}
        {timelineData && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Successful</p>
                  <h3 className="text-2xl font-bold text-green-600">
                    {timelineData.summary.totalSuccessful.toLocaleString()}
                  </h3>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <MessageSquare className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Failed</p>
                  <h3 className="text-2xl font-bold text-red-600">
                    {timelineData.summary.totalFailed.toLocaleString()}
                  </h3>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <TrendingUp className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Success Rate</p>
                  <h3 className="text-2xl font-bold text-blue-600">
                    {timelineData.summary.successRate}%
                  </h3>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Chart 1: Messages Timeline */}
        <Card className="mb-6 shadow-sm">
          <div className="p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">
                  Messages Sent Over Time
                </h3>
                <p className="text-sm text-gray-600">
                  Track your messaging activity and success rate
                </p>
              </div>
              <div className="flex gap-3">
                <Dropdown
                  value={period}
                  onChange={(e) => setPeriod(e.value)}
                  options={periodOptions}
                  className="w-32"
                />
                <Dropdown
                  value={range}
                  onChange={(e) => setRange(e.value)}
                  options={rangeOptions}
                  className="w-40"
                />
              </div>
            </div>
            {timelineData && (
              <div style={{ height: "400px" }}>
                <Chart
                  type="line"
                  data={timelineData}
                  options={timelineChartOptions}
                />
              </div>
            )}
          </div>
        </Card>

        {/* Chart 2: Usage Dashboard */}
        {usageData && (
          <Card className="mb-6 shadow-sm">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    Subscription Usage
                  </h3>
                  <p className="text-sm text-gray-600">
                    Current plan: {usageData.subscription.planName} •{" "}
                    {usageData.subscription.daysRemaining} days remaining
                  </p>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {new Date(
                      usageData.subscription.currentPeriodStart
                    ).toLocaleDateString()}{" "}
                    -{" "}
                    {new Date(
                      usageData.subscription.currentPeriodEnd
                    ).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Customers Usage */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-blue-100 rounded-full">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        Customers
                      </p>
                      <p className="text-lg font-bold text-gray-900">
                        {usageData.customers.used} /{" "}
                        {usageData.customers.isUnlimited
                          ? "∞"
                          : usageData.customers.limit}
                      </p>
                    </div>
                  </div>
                  {!usageData.customers.isUnlimited && (
                    <ProgressBar
                      value={Math.min(usageData.customers.percentage, 100)}
                      showValue={false}
                      color={
                        usageData.customers.percentage >= 90
                          ? "#ef4444"
                          : usageData.customers.percentage >= 70
                          ? "#f59e0b"
                          : "#10b981"
                      }
                      className="h-3"
                    />
                  )}
                  {!usageData.customers.isUnlimited && (
                    <p className="text-xs text-gray-500 mt-2">
                      {usageData.customers.percentage.toFixed(1)}% used
                    </p>
                  )}
                </div>

                {/* Groups Usage */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-green-100 rounded-full">
                      <FolderOpen className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        Groups
                      </p>
                      <p className="text-lg font-bold text-gray-900">
                        {usageData.groups.used} /{" "}
                        {usageData.groups.isUnlimited
                          ? "∞"
                          : usageData.groups.limit}
                      </p>
                    </div>
                  </div>
                  {!usageData.groups.isUnlimited && (
                    <ProgressBar
                      value={Math.min(usageData.groups.percentage, 100)}
                      showValue={false}
                      color={
                        usageData.groups.percentage >= 90
                          ? "#ef4444"
                          : usageData.groups.percentage >= 70
                          ? "#f59e0b"
                          : "#10b981"
                      }
                      className="h-3"
                    />
                  )}
                  {!usageData.groups.isUnlimited && (
                    <p className="text-xs text-gray-500 mt-2">
                      {usageData.groups.percentage.toFixed(1)}% used
                    </p>
                  )}
                </div>

                {/* Messages Usage */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-purple-100 rounded-full">
                      <MessageSquare className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        Messages (This Period)
                      </p>
                      <p className="text-lg font-bold text-gray-900">
                        {usageData.messages.used} /{" "}
                        {usageData.messages.isUnlimited
                          ? "∞"
                          : usageData.messages.limit}
                      </p>
                    </div>
                  </div>
                  {!usageData.messages.isUnlimited && (
                    <ProgressBar
                      value={Math.min(usageData.messages.percentage, 100)}
                      showValue={false}
                      color={
                        usageData.messages.percentage >= 90
                          ? "#ef4444"
                          : usageData.messages.percentage >= 70
                          ? "#f59e0b"
                          : "#10b981"
                      }
                      className="h-3"
                    />
                  )}
                  {!usageData.messages.isUnlimited && (
                    <p className="text-xs text-gray-500 mt-2">
                      {usageData.messages.percentage.toFixed(1)}% used •{" "}
                      {usageData.messages.totalAllTime.toLocaleString()} total
                      all time
                    </p>
                  )}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Chart 3: Message Status Breakdown */}
        {statusData && statusData.labels.length > 0 && (
          <Card className="shadow-sm">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-1">
                Message Status Distribution
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Breakdown of all messages by delivery status
              </p>
              <div className="flex flex-col md:flex-row items-center">
                <div
                  style={{ height: "300px", width: "100%", maxWidth: "400px" }}
                >
                  <Chart
                    type="doughnut"
                    data={statusData}
                    options={statusChartOptions}
                  />
                </div>
                <div className="flex-1 ml-0 md:ml-8 mt-6 md:mt-0">
                  <div className="space-y-3">
                    {statusData.labels.map((label, index) => (
                      <div
                        key={label}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{
                              backgroundColor:
                                statusData.datasets[0].backgroundColor[index],
                            }}
                          ></div>
                          <span className="font-medium text-gray-900">
                            {label}
                          </span>
                        </div>
                        <span className="font-bold text-gray-900">
                          {statusData.datasets[0].data[index].toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Analytics;
